import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import '../styles/EmlakRapor.css';
import TrDateInput from '../components/TrDateInput';

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

interface PortfoyItem {
  portfoyID: number;
  baslik: string;
  adres: string | null;
  fiyat: number;
  m2: number;
  ilanNo: string;
  tur: string;
  durum: string;
  eklenmeTarihi: string;
  aktifMi: boolean;
}

interface SunumItem {
  sunumID: number;
  takipNo: string;
  cariID: number;
  cariAdSoyad: string;
  cariFirma: string | null;
  cariTelefon: string | null;
  olusturanKullaniciID: number;
  olusturanAdSoyad: string;
  baslik: string | null;
  durum: string;
  olusturmaTarihi: string;
  aktifMi: boolean;
  portfoyAdet: number;
  portfoyler: {
    sunumPortfoyID: number;
    portfoyID: number;
    portfoyBaslik: string;
    portfoyIlanNo: string;
    durum: string;
    musteriDurum: string;
  }[];
}

interface PortfoyRaporSatir {
  portfoy: PortfoyItem;
  sunumSayisi: number;
  sunumlar: {
    sunumID: number;
    takipNo: string;
    cariAdSoyad: string;
    cariFirma: string | null;
    cariTelefon: string | null;
    durum: string;
    olusturmaTarihi: string;
    portfoyDurum: string;
    musteriDurum: string;
  }[];
}

const turLabels: Record<string, string> = {
  'Konut': 'Konut', 'IsYeri': 'İş Yeri', 'Arsa': 'Arsa', 'Tarla': 'Tarla'
};
const durumLabels: Record<string, string> = {
  'Satilik': 'Satılık', 'Kiralik': 'Kiralık', 'Portfoy': 'Portföy'
};

const sunumDurumColors: Record<string, string> = {
  'Taslak': '#64748b', 'Aktif': '#16a34a', 'Tamamlandı': '#2563eb', 'İptal': '#ef4444'
};

const EmlakRapor: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const reportRef = useRef<HTMLDivElement>(null);
  const [kurumsal, setKurumsal] = useState<any>(null);

  // Data
  const [portfoyler, setPortfoyler] = useState<PortfoyItem[]>([]);
  const [sunumlar, setSunumlar] = useState<SunumItem[]>([]);

  // Filters
  const [baslangic, setBaslangic] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return d.toISOString().split('T')[0];
  });
  const [bitis, setBitis] = useState(() => new Date().toISOString().split('T')[0]);

  // Modal
  const [modalPortfoy, setModalPortfoy] = useState<PortfoyRaporSatir | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [pRes, sRes, kRes] = await Promise.all([
        axiosInstance.get('/api/EmlakPortfoy'),
        axiosInstance.get('/api/EmlakSunumlari'),
        axiosInstance.get('/api/Kurumsal/1').catch(() => ({ data: null }))
      ]);
      setPortfoyler(pRes.data);
      setSunumlar(sRes.data);
      setKurumsal(kRes.data);
    } catch {
      toast.error('Rapor verileri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Tarih filtreleme
  const filteredSunumlar = sunumlar.filter(s => {
    const t = new Date(s.olusturmaTarihi).getTime();
    return t >= new Date(baslangic).getTime() && t <= new Date(bitis + 'T23:59:59').getTime();
  });

  const filteredPortfoyler = portfoyler.filter(p => {
    const t = new Date(p.eklenmeTarihi).getTime();
    return t >= new Date(baslangic).getTime() && t <= new Date(bitis + 'T23:59:59').getTime();
  });

  // === İSTATİSTİKLER ===
  const toplamPortfoy = portfoyler.length;
  const yeniPortfoy = filteredPortfoyler.length;
  const toplamSunum = filteredSunumlar.length;
  const aktifSunum = filteredSunumlar.filter(s => s.durum === 'Aktif').length;

  // Portföy - Sunum ilişkisi
  const portfoyRapor: PortfoyRaporSatir[] = portfoyler.map(p => {
    const ilgiliSunumlar = sunumlar
      .filter(s => s.portfoyler.some(sp => sp.portfoyID === p.portfoyID))
      .map(s => {
        const sp = s.portfoyler.find(sp => sp.portfoyID === p.portfoyID)!;
        return {
          sunumID: s.sunumID,
          takipNo: s.takipNo,
          cariAdSoyad: s.cariAdSoyad,
          cariFirma: s.cariFirma,
          cariTelefon: s.cariTelefon,
          durum: s.durum,
          olusturmaTarihi: s.olusturmaTarihi,
          portfoyDurum: sp.durum,
          musteriDurum: sp.musteriDurum
        };
      });
    return { portfoy: p, sunumSayisi: ilgiliSunumlar.length, sunumlar: ilgiliSunumlar };
  }).sort((a, b) => b.sunumSayisi - a.sunumSayisi);

  // Sunum durum dağılımı
  const sunumDurumDagilimi = filteredSunumlar.reduce((acc, s) => {
    acc[s.durum] = (acc[s.durum] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Personel bazlı sunum sayısı
  const personelSunum = filteredSunumlar.reduce((acc, s) => {
    const key = s.olusturanAdSoyad || 'Bilinmiyor';
    if (!acc[key]) acc[key] = { count: 0, sunumlar: [] as SunumItem[] };
    acc[key].count++;
    acc[key].sunumlar.push(s);
    return acc;
  }, {} as Record<string, { count: number; sunumlar: SunumItem[] }>);

  const personelList = Object.entries(personelSunum)
    .sort((a, b) => b[1].count - a[1].count);
  const maxPersonelCount = personelList.length > 0 ? personelList[0][1].count : 1;

  // Portföy tür dağılımı
  const portfoyTurDagilimi = portfoyler.reduce((acc, p) => {
    const label = turLabels[p.tur] || p.tur;
    acc[label] = (acc[label] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Chart verileri
  const sunumDurumChartData = {
    labels: Object.keys(sunumDurumDagilimi),
    datasets: [{
      data: Object.values(sunumDurumDagilimi),
      backgroundColor: Object.keys(sunumDurumDagilimi).map(k => sunumDurumColors[k] || '#94a3b8'),
      borderWidth: 0,
      hoverOffset: 6
    }]
  };

  const portfoyTurChartData = {
    labels: Object.keys(portfoyTurDagilimi),
    datasets: [{
      label: 'Portföy Sayısı',
      data: Object.values(portfoyTurDagilimi),
      backgroundColor: ['#cd6906', '#2563eb', '#16a34a', '#9333ea', '#ef4444'],
      borderWidth: 0,
      borderRadius: 6
    }]
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const, labels: { padding: 16, font: { size: 12 } } }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false }
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1 } }
    }
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  const downloadPDF = async () => {
    const el = reportRef.current;
    if (!el) return;
    try {
      toast.info('PDF hazırlanıyor...', { autoClose: 2000 });
      const canvas = await html2canvas(el, { scale: 2.5, useCORS: true, backgroundColor: '#ffffff' });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const margin = 10;
      const pdfW = 210;
      const printableW = pdfW - margin * 2;
      const pxPerMm = canvas.width / printableW;
      const pageH = 297 - margin * 2;
      const totalPages = Math.ceil((canvas.height / pxPerMm) / pageH);
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) pdf.addPage();
        const srcY = i * pageH * pxPerMm;
        const srcH = Math.min(pageH * pxPerMm, canvas.height - srcY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width; sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext('2d');
        if (ctx) { ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height); ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH); }
        pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', margin, margin, printableW, srcH / pxPerMm);
      }
      pdf.save(`EmlakRapor_${baslangic}_${bitis}.pdf`);
      toast.success('PDF indirildi!');
    } catch { toast.error('PDF oluşturulurken hata oluştu!'); }
  };

  const getSunumBadgeStyle = (durum: string): React.CSSProperties => {
    const map: Record<string, React.CSSProperties> = {
      'Aktif':       { background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0' },
      'Tamaland\u0131':  { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
      '\u0130ptal':      { background: '#fff1f2', color: '#e11d48', border: '1px solid #fecdd3' },
      'Taslak':      { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' },
    };
    return { ...( map[durum] || map['Taslak'] ), borderRadius: 9999, padding: '2px 10px', fontSize: 12, fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap' };
  };

  if (loading) {
    return (
      <div className="er-container">
        <div className="er-loading">
          <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="#cd6906" strokeWidth={2.2} strokeLinecap="round" style={{ animation: 'spin 0.9s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.22-8.56"/></svg>
          Yükleniyor...
        </div>
      </div>
    );
  }

  return (
    <div className="er-container">
      {/* Header */}
      <div className="er-header">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827', letterSpacing: '-0.025em', lineHeight: 1.2 }}>Emlak Rapor</h1>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280', fontWeight: 500 }}>Portföy, sunum ve satış verilerinin özet analizi</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="er-btn-pdf" onClick={downloadPDF}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1={12} y1={18} x2={12} y2={12}/><polyline points="9 15 12 18 15 15"/></svg>
            PDF İndir
          </button>
          <button className="er-btn-print" onClick={() => window.print()}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
            Yazdır
          </button>
        </div>
      </div>

      {/* Filtreler */}
      <div className="er-filter-bar">
        <div className="er-filter-group">
          <label>Başlangıç</label>
          <TrDateInput value={baslangic} onChange={e => setBaslangic(e.target.value)} />
        </div>
        <div className="er-filter-group">
          <label>Bitiş</label>
          <TrDateInput value={bitis} onChange={e => setBitis(e.target.value)} />
        </div>
        <button className="er-btn-filter" onClick={fetchData} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
          Yenile
        </button>
      </div>

      {/* İçerik - PDF için ref */}
      <div ref={reportRef}>

          {/* İstatistik Kartları */}
          <div className="er-stats-row">
            <div className="er-stat-card">
              <div className="er-stat-icon" style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe' }}>
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1={12} y1={12} x2={12} y2={16}/><line x1={10} y1={14} x2={14} y2={14}/></svg>
              </div>
              <div className="er-stat-info">
                <h4>Toplam Portföy</h4>
                <div className="er-stat-value">{toplamPortfoy}</div>
              </div>
              <div className="er-stat-icon" style={{ background: '#fff7ed', border: '1.5px solid #fed7aa' }}>
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#cd6906" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx={12} cy={12} r={10}/><line x1={12} y1={8} x2={12} y2={16}/><line x1={8} y1={12} x2={16} y2={12}/></svg>
              </div>
              <div className="er-stat-info">
                <h4>YENİ PORTFÖY <span style={{ fontWeight: 500, opacity: 0.7 }}>(dönem)</span></h4>
                <div className="er-stat-value">{yeniPortfoy}</div>
              </div>
            </div>
            <div className="er-stat-card">
              <div className="er-stat-icon" style={{ background: '#f0fdf4', border: '1.5px solid #bbf7d0' }}>
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
              </div>
              <div className="er-stat-info">
                <h4>SUNUM SAYISI <span style={{ fontWeight: 500, opacity: 0.7 }}>(dönem)</span></h4>
                <div className="er-stat-value">{toplamSunum}</div>
              </div>
            </div>
            <div className="er-stat-card">
              <div className="er-stat-icon" style={{ background: '#f5f3ff', border: '1.5px solid #ddd6fe' }}>
                <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <div className="er-stat-info">
                <h4>Aktif Sunum</h4>
                <div className="er-stat-value">{aktifSunum}</div>
              </div>
            </div>
          </div>

          {/* Grafikler */}
          <div className="er-charts-row">
            <div className="er-chart-card">
              <h3><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg> Sunum Durum Dağılımı</h3>
              <div className="er-chart-wrap">
                {Object.keys(sunumDurumDagilimi).length > 0 ? (
                  <Doughnut data={sunumDurumChartData} options={doughnutOptions} />
                ) : (
                  <div className="er-no-data">Veri yok</div>
                )}
              </div>
            </div>
            <div className="er-chart-card">
              <h3><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1={18} y1={20} x2={18} y2={10}/><line x1={12} y1={20} x2={12} y2={4}/><line x1={6} y1={20} x2={6} y2={14}/><line x1={2} y1={20} x2={22} y2={20}/></svg> Portföy Tür Dağılımı</h3>
              <div className="er-chart-wrap">
                {Object.keys(portfoyTurDagilimi).length > 0 ? (
                  <Bar data={portfoyTurChartData} options={barOptions} />
                ) : (
                  <div className="er-no-data">Veri yok</div>
                )}
              </div>
            </div>
          </div>

          {/* Personel Performansı */}
          <div className="er-section">
            <div className="er-section-header">
              <h2 className="er-section-title"><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx={12} cy={7} r={4}/></svg> Personel Sunum Performansı</h2>
            </div>
            {personelList.length > 0 ? (
              personelList.map(([name, data]) => (
                <div key={name} className="er-personel-bar">
                  <span className="er-personel-name">{name}</span>
                  <div className="er-personel-progress">
                    <div className="er-personel-progress-fill" style={{ width: `${Math.max((data.count / maxPersonelCount) * 100, 8)}%` }} />
                  </div>
                  <span className="er-personel-count">{data.count}</span>
                </div>
              ))
            ) : (
              <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Bu dönemde sunum bulunmuyor.</div>
            )}
          </div>

          {/* Dönem Sunumları */}
          <div className="er-section">
            <div className="er-section-header">
              <h2 className="er-section-title"><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><line x1={8} y1={6} x2={21} y2={6}/><line x1={8} y1={12} x2={21} y2={12}/><line x1={8} y1={18} x2={21} y2={18}/><line x1={3} y1={6} x2={3.01} y2={6}/><line x1={3} y1={12} x2={3.01} y2={12}/><line x1={3} y1={18} x2={3.01} y2={18}/></svg> Dönem Sunumları ({filteredSunumlar.length})</h2>
            </div>
            <div className="er-table-wrap">
              <table className="er-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Takip No</th>
                    <th>Müşteri</th>
                    <th>Personel</th>
                    <th>Durum</th>
                    <th style={{ textAlign: 'right' }}>Portföy</th>
                    <th style={{ textAlign: 'right' }}>Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSunumlar.length > 0 ? filteredSunumlar.map((s, i) => (
                    <tr key={s.sunumID} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                      <td>{i + 1}</td>
                      <td><span className="er-link" onClick={() => navigate(`/emlak-sunumlari/${s.sunumID}`)}>{s.takipNo}</span></td>
                      <td>{s.cariAdSoyad}</td>
                      <td>{s.olusturanAdSoyad}</td>
                      <td><span style={getSunumBadgeStyle(s.durum)}>{s.durum}</span></td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: '#1e293b' }}>{s.portfoyAdet}</td>
                      <td style={{ textAlign: 'right', color: '#64748b', fontSize: 12 }}>{formatDate(s.olusturmaTarihi)}</td>
                    </tr>
                  )) : (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>Bu dönemde sunum bulunmuyor.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Portföy & Sunum İlişkisi */}
          <div className="er-section">
            <div className="er-section-header">
              <h2 className="er-section-title"><svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg> Portföy & Sunum İlişkisi</h2>
            </div>
            <div className="er-table-wrap">
              <table className="er-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>İlan No</th>
                    <th>Başlık</th>
                    <th>Tür</th>
                    <th>Durum</th>
                    <th style={{ textAlign: 'right' }}>Fiyat</th>
                    <th style={{ textAlign: 'center' }}>Sunum</th>
                    <th className="er-no-print">Detay</th>
                  </tr>
                </thead>
                <tbody>
                  {portfoyRapor.map((pr, i) => (
                    <tr key={pr.portfoy.portfoyID} style={{ breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                      <td>{i + 1}</td>
                      <td><span className="er-link" onClick={() => navigate(`/emlak-portfoy/${pr.portfoy.portfoyID}`)}>{pr.portfoy.ilanNo}</span></td>
                      <td>{pr.portfoy.baslik}</td>
                      <td>{turLabels[pr.portfoy.tur] || pr.portfoy.tur}</td>
                      <td>
                        {(() => {
                          const d = durumLabels[pr.portfoy.durum] || pr.portfoy.durum;
                          const s: Record<string, React.CSSProperties> = {
                            'Satılık': { background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' },
                            'Kiralık': { background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe' },
                            'Portföy': { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' },
                          };
                          const bs = s[d] || s['Portföy'];
                          return <span style={{ ...bs, borderRadius: 9999, padding: '2px 9px', fontSize: 11.5, fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap' }}>{d}</span>;
                        })()}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{pr.portfoy.fiyat.toLocaleString('tr-TR')} ₺</td>
                      <td style={{ textAlign: 'center', fontWeight: 700, color: pr.sunumSayisi > 0 ? '#cd6906' : '#cbd5e1', fontVariantNumeric: 'tabular-nums' }}>{pr.sunumSayisi}</td>
                      <td className="er-no-print">
                        {pr.sunumSayisi > 0 && (
                          <button className="er-link" style={{ background: 'none', border: 'none', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }} onClick={() => setModalPortfoy(pr)}>
                            <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx={12} cy={12} r={3}/></svg>
                            Görüntüle
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

      </div> {/* /reportRef */}

      {/* Modal - Portföy Sunumları Detay */}
      {modalPortfoy && (
        <div className="er-modal-overlay" onClick={() => setModalPortfoy(null)}>
          <div className="er-modal" onClick={e => e.stopPropagation()}>
            <div className="er-modal-header">
              <span className="er-modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 28, height: 28, borderRadius: 7, background: '#fff7ed', border: '1.5px solid #fed7aa', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="#cd6906" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                </div>
                {modalPortfoy.portfoy.baslik} — Sunumlar ({modalPortfoy.sunumSayisi})
              </span>
              <button className="er-modal-close" onClick={() => setModalPortfoy(null)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 7, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', color: '#64748b' }}>
                <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1={18} y1={6} x2={6} y2={18}/><line x1={6} y1={6} x2={18} y2={18}/></svg>
              </button>
            </div>
            <div className="er-modal-body">
              <div style={{ marginBottom: 16, fontSize: 13, color: '#64748b' }}>
                <strong>İlan No:</strong> {modalPortfoy.portfoy.ilanNo} &nbsp;|&nbsp;
                <strong>Tür:</strong> {turLabels[modalPortfoy.portfoy.tur] || modalPortfoy.portfoy.tur} &nbsp;|&nbsp;
                <strong>Fiyat:</strong> {modalPortfoy.portfoy.fiyat.toLocaleString('tr-TR')} ₺
              </div>
              <div className="er-sunum-list">
                {modalPortfoy.sunumlar.map(s => (
                  <div key={s.sunumID} className="er-sunum-item">
                    <div className="er-sunum-item-left">
                      <div className="er-sunum-item-title">
                        <span className="er-link" onClick={() => { setModalPortfoy(null); navigate(`/emlak-sunumlari/${s.sunumID}`); }}>{s.takipNo}</span>
                      </div>
                      <div className="er-sunum-item-meta">
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx={12} cy={7} r={4}/></svg>{s.cariAdSoyad}</span>
                        {s.cariFirma && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>{s.cariFirma}</span>}
                        {s.cariTelefon && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 11.87 19a19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 3.1 4.1 2 2 0 0 1 5.09 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L9.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 23 17z"/></svg>{s.cariTelefon}</span>}
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1={16} y1={2} x2={16} y2={6}/><line x1={8} y1={2} x2={8} y2={6}/><line x1={3} y1={10} x2={21} y2={10}/></svg>{formatDate(s.olusturmaTarihi)}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <span style={getSunumBadgeStyle(s.durum)}>{s.durum}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmlakRapor;
